import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import BootstrapTable from 'react-bootstrap-table-next'
import filterFactory from 'react-bootstrap-table2-filter'
import moment from 'moment-timezone'

import componentStyles from 'assets/theme/views/admin/react-bs-table.js'

const useStyles = makeStyles(componentStyles)

// Leads allocated before this date do not have a persisted lead.leadPrice.
// See scripts/backfill-lead-price.js for the backfill covering 2026.
const SOLD_PRICE_AVAILABLE_FROM = moment.tz('2026-01-01', 'Europe/Dublin').startOf('day')

// Column header with an info tooltip.
function HeaderWithTooltip({ label, tooltip }) {
    return (
        <Box
            component='span'
            display='inline-flex'
            alignItems='center'
            justifyContent='flex-end'
            style={{ gap: '4px' }}
        >
            <span>{label}</span>
            <Tooltip title={tooltip} arrow interactive>
                <InfoOutlinedIcon
                    style={{ fontSize: '0.9rem', opacity: 0.6, cursor: 'help' }}
                    onClick={(e) => e.stopPropagation()}
                />
            </Tooltip>
        </Box>
    )
}

function EEProductsTable({ productBreakdown, fromDate }) {
    const classes = useStyles()

    // Footer totals
    let generatedSum = 0
    let rejectedSum = 0
    let unallocatedSum = 0
    let soldSum = 0
    let soldMissingPriceSum = 0
    let revenueSoldSum = 0
    let revenueDeliverySum = 0
    for (const row of productBreakdown) {
        generatedSum += parseInt(row.generated) || 0
        rejectedSum += parseInt(row.rejected) || 0
        unallocatedSum += parseInt(row.unallocated) || 0
        soldSum += parseInt(row.sold) || 0
        soldMissingPriceSum += parseInt(row.soldMissingPrice) || 0
        revenueSoldSum += parseFloat(row.revenueSold) || 0
        revenueDeliverySum += parseFloat(row.revenueDelivery) || 0
    }

    // Range-based warnings
    const rangeStart = fromDate ? moment(fromDate) : null
    const includesPre2026 = rangeStart && rangeStart.isBefore(SOLD_PRICE_AVAILABLE_FROM)
    const hasMissingSoldPrices = soldMissingPriceSum > 0

    const colStyle = { whiteSpace: 'normal' }
    const numStyle = { ...colStyle, textAlign: 'right' }

    const revenueSoldTooltip =
        'Sum of the actual sold price stored on each lead at the moment it was allocated. ' +
        'This is the true billed revenue (includes €0 replacements and any historical price changes). ' +
        'Only available for leads allocated from 1 Jan 2026 onwards.'

    const revenueDeliveryTooltip =
        'Sum of each sold lead counted at the company/product\u2019s CURRENT delivery rate ' +
        '(from bd-energy-deliveries.leadPrice). Useful as a projection at today\u2019s rates, ' +
        'but does not reflect historical price changes or €0 replacement leads.'

    const columns = [
        {
            dataField: 'product',
            text: 'Product',
            headerStyle: () => colStyle,
            style: colStyle,
            formatter: (cell, row) => <Box className='primary-text'>{row.product}</Box>,
            footer: 'Total'
        },
        {
            dataField: 'generated',
            text: 'Generated',
            headerStyle: () => numStyle,
            style: numStyle,
            formatter: (cell, row) => <>{row.generated}</>,
            footer: String(generatedSum),
            footerStyle: () => numStyle
        },
        {
            dataField: 'rejected',
            text: 'Rejected',
            headerStyle: () => numStyle,
            style: numStyle,
            formatter: (cell, row) => <>{row.rejected}</>,
            footer: String(rejectedSum),
            footerStyle: () => numStyle
        },
        {
            dataField: 'unallocated',
            text: 'Unallocated',
            headerStyle: () => numStyle,
            style: numStyle,
            formatter: (cell, row) => <>{row.unallocated}</>,
            footer: String(unallocatedSum),
            footerStyle: () => numStyle
        },
        {
            dataField: 'sold',
            text: 'Sold',
            headerStyle: () => numStyle,
            style: numStyle,
            formatter: (cell, row) => <>{row.sold}</>,
            footer: String(soldSum),
            footerStyle: () => numStyle
        },
        {
            dataField: 'revenueDelivery',
            text: 'Revenue (Delivery Price)',
            headerFormatter: () => (
                <HeaderWithTooltip
                    label='Revenue (Delivery Price)'
                    tooltip={revenueDeliveryTooltip}
                />
            ),
            headerStyle: () => numStyle,
            style: numStyle,
            sort: true,
            formatter: (cell, row) => <>€{(row.revenueDelivery || 0).toFixed(2)}</>,
            footer: '€' + revenueDeliverySum.toFixed(2),
            footerStyle: () => numStyle
        },
        {
            dataField: 'revenueSold',
            text: 'Revenue (Sold Price)',
            headerFormatter: () => (
                <HeaderWithTooltip
                    label='Revenue (Sold Price)'
                    tooltip={revenueSoldTooltip}
                />
            ),
            headerStyle: () => numStyle,
            style: numStyle,
            sort: true,
            formatter: (cell, row) => {
                const value = <>€{(row.revenueSold || 0).toFixed(2)}</>
                if (row.soldMissingPrice > 0) {
                    return (
                        <Tooltip
                            title={`${row.soldMissingPrice} of ${row.sold} sold ${row.product} leads have no persisted sold price and are counted as €0 here. See the note above the table.`}
                            arrow
                        >
                            <Box component='span' style={{ color: '#f5365c', cursor: 'help' }}>
                                {value}*
                            </Box>
                        </Tooltip>
                    )
                }
                return value
            },
            footer: '€' + revenueSoldSum.toFixed(2),
            footerStyle: () => numStyle
        }
    ]

    return (
        <Card>
            <>
                <CardHeader
                    subheader={
                        <>
                            <Box display='flex' style={{ justifyContent: 'space-between' }}>
                                <Box>
                                    <Box
                                        component={Typography}
                                        variant='h2'
                                        marginBottom='0!important'
                                    >
                                        <Box component='span'>Products Breakdown</Box>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    }
                    classes={{ root: classes.cardHeaderRoot }}
                ></CardHeader>

                {(includesPre2026 || hasMissingSoldPrices) && (
                    <Box
                        padding='0.75rem 1.5rem'
                        margin='1rem 1.5rem'
                        borderRadius='4px'
                        style={{
                            backgroundColor: '#fff5f7',
                            borderLeft: '3px solid #f5365c'
                        }}
                    >
                        <Box
                            component={Typography}
                            variant='body2'
                            style={{ color: '#32325d', fontSize: '0.8125rem' }}
                        >
                            <Box component='strong' marginRight='0.25rem'>Note:</Box>
                            {includesPre2026 && (
                                <>
                                    The selected range includes dates before 1 Jan 2026. Leads
                                    allocated before then do not have a persisted sold price, so
                                    <Box component='strong' marginX='0.25rem'>Revenue (Sold Price)</Box>
                                    undercounts for that portion of the range. Volumes (Generated /
                                    Sold / etc.) are accurate.
                                    <Box component='strong' marginLeft='0.25rem'>Revenue (Delivery Price)</Box>
                                    &nbsp;reflects what these leads would be worth at today&rsquo;s
                                    rates and is the better figure for pre-2026 ranges.
                                </>
                            )}
                            {!includesPre2026 && hasMissingSoldPrices && (
                                <>
                                    {soldMissingPriceSum} sold lead{soldMissingPriceSum === 1 ? '' : 's'}
                                    &nbsp;in this range {soldMissingPriceSum === 1 ? 'is' : 'are'} missing
                                    a persisted sold price and{' '}
                                    {soldMissingPriceSum === 1 ? 'is' : 'are'} counted as &euro;0 in
                                    <Box component='strong' marginX='0.25rem'>Revenue (Sold Price)</Box>
                                    (marked with * in the affected rows). Re-run
                                    <Box
                                        component='code'
                                        marginX='0.25rem'
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        scripts/backfill-lead-price.js
                                    </Box>
                                    to fix.
                                </>
                            )}
                        </Box>
                    </Box>
                )}

                <div className='table-container'>
                    <BootstrapTable
                        keyField='product'
                        data={productBreakdown}
                        columns={columns}
                        filter={filterFactory()}
                        noDataIndication={<Box>No data for this period</Box>}
                        wrapperClasses='equal-width-products-table'
                    />
                </div>
            </>
        </Card>
    )
}

export default EEProductsTable
